import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SignupPreviewService } from '../components/dinamic-signup/services/signup-preview.service';
import {
  PREVIEW_ERUT,
  PREVIEW_FACIAL,
  PREVIEW_OCR_CHILE,
  PREVIEW_PEP_CHILE,
} from '../components/dinamic-signup/constants/preview-empresa.fixtures';

/**
 * Estructura del wrapper de respuesta del backend KYC
 * Todas las respuestas vienen envueltas en esta estructura
 */
export interface KycApiWrapper<T> {
  success: boolean;
  data: T;
  metadata: {
    timestamp: string;
    service: string;
    processingTimeMs: number;
    version: string;
  };
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
}

/**
 * Resultado de `/otp/send` y `/otp/resend`. Clasificar SIEMPRE por esto y no
 * por el texto de `message`, que es solo para mostrar.
 *
 * - SENT: el código salió.
 * - COOLDOWN / RATE_LIMITED: todavía no se puede pedir otro; traen
 *   `resendInSeconds`. RATE_LIMITED es carga general: el número está bien.
 * - NOT_ON_WHATSAPP: el teléfono no tiene WhatsApp. Es el único rechazo en el
 *   que tiene sentido pedirle al usuario que cambie el número.
 * - UNAVAILABLE (WhatsApp caído), SEND_FAILED (WhatsApp rechazó el envío) y
 *   ERROR (interno o de red): reintentables.
 *
 * En cualquier rechazo el código anterior SIGUE vigente en el backend: si hay
 * uno, la respuesta trae su `expiresAt` / `expiresInSeconds`.
 */
export type SendOtpCode =
  | 'SENT'
  | 'COOLDOWN'
  | 'RATE_LIMITED'
  | 'UNAVAILABLE'
  | 'NOT_ON_WHATSAPP'
  | 'SEND_FAILED'
  | 'ERROR';

/**
 * Resultado de `/otp/verify`. EXPIRED, BLOCKED (sin intentos) y NO_PENDING
 * dejan el código inutilizable: hay que pedir otro. INVALID_CODE trae
 * `remainingAttempts`.
 */
export type VerifyOtpCode =
  | 'VERIFIED'
  | 'INVALID_CODE'
  | 'EXPIRED'
  | 'BLOCKED'
  | 'NO_PENDING'
  | 'ERROR';

export interface SendOtpResponse {
  success: boolean;
  code: SendOtpCode;
  message: string;
  expiresAt?: string;
  /**
   * Vida restante del código en segundos al momento de la respuesta. Preferir
   * esto sobre `expiresAt`: al ser una duración no depende de que el reloj del
   * browser esté sincronizado con el del servidor.
   */
  expiresInSeconds?: number;
  /** Segundos hasta poder reenviar (cooldown anti-ban del backend). */
  resendInSeconds?: number;
}

export interface VerifyOtpResponse {
  success: boolean;
  code: VerifyOtpCode;
  message: string;
  remainingAttempts?: number;
}

export interface OcrChileResponse {
  tipoDocumento: string;
  pais: string;
  campos: {
    apellidos: string;
    nombres: string;
    run: string;
    runSinFormato: string;
    nacionalidad: string;
    sexo: string;
    fechaNacimiento: string;
    fechaNacimientoISO: string;
    fechaVencimiento: string;
    fechaVencimientoISO: string;
    fechaEmision?: string;
    fechaEmisionISO?: string;
    numeroDocumento?: string;
  };
  validacion: {
    runValido: boolean;
    estaVencido: boolean;
    confianzaOCR: number;
    camposCompletos: boolean;
    diasHastaVencimiento?: number;
    advertencias?: string[];
  };
  fotoPersona: string;
  verificacionSanciones?: {
    hasMatches: boolean;
    riskLevel: string;
    matches: any[];
  };
  metadata?: {
    processingTimeMs: number;
    servicesUsed: string[];
    imageQuality: string;
  };
}

export interface OcrVenezuelaResponse {
  tipoDocumento: string;
  pais: string;
  campos: {
    apellidos: string;
    nombres: string;
    tipoCedula: string;
    numeroCedula: string;
    numeroCedulaSinFormato: string;
    numeroExpedicion?: string;
    nacionalidad: string;
    sexo: string;
    estadoCivil?: string;
    fechaNacimiento: string;
    fechaNacimientoISO: string;
    fechaExpedicion?: string;
    fechaExpedicionISO?: string;
    fechaVencimiento?: string;
    fechaVencimientoISO?: string;
  };
  validacion: {
    cedulaValida: boolean;
    estaVencido: boolean;
    confianzaOCR: number;
    camposCompletos: boolean;
    fechasCoherentes?: boolean;
    diasHastaVencimiento?: number;
    advertencias?: string[];
  };
  fotoPersona: string;
  verificacionSanciones?: {
    hasMatches: boolean;
    riskLevel: string;
    matches: any[];
  };
  metadata?: {
    processingTimeMs: number;
    servicesUsed: string[];
    imageQuality: string;
  };
}

export interface OcrRegistroData {
  cognitoId: string;
  tipo: 'PERSONA' | 'EMPRESA';
  email?: string;
  whatsapp?: string;
  ip?: string;
}

export interface CountryRisk {
  isBlackListed: boolean;
  isGreyListed: boolean;
  riskLevel: 'CRITICAL' | 'HIGH' | 'NONE';
  listName: string;
  countryCode: string;
}

export interface SanctionsCheckResult {
  hasMatches: boolean;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  matches: SanctionsMatch[];
  countryRisk?: CountryRisk;
  listsChecked: string[];
  metadata: {
    timestamp: string;
    checkedName: string;
    checkedNationality?: string;
    processingTimeMs: number;
  };
}

export interface SanctionsMatch {
  list: string;
  matchedName: string;
  aliases?: string[];
  score: number;
  nationality?: string;
  dateOfBirth?: string;
  type: string;
  programs: string[];
  remarks?: string;
  referenceUrl?: string;
}

export interface PepChileCheckRequest {
  dni: string;
  personType?: 'natural' | 'legal';
  name?: string;
  fatherName?: string;
  motherName?: string;
  nationality?: string;
  country?: string;
}

export interface PepListDetail {
  listName: string;
  coincidence: boolean;
  risk?: 'high' | 'medium' | 'low';
  lastChecked?: string;
}

export interface PepChileCheckResult {
  isPep: boolean;
  isFuncionarioPublico: boolean;
  pepLevel: number;
  effectiveRisk?: string;
  listsChecked: PepListDetail[];
  dni: string;
  fullName?: string;
  metadata: {
    timestamp: string;
    checkedDni: string;
    processingTimeMs: number;
  };
}

// ==================== e-RUT CHILE ====================

/**
 * Veredicto del analisis del e-RUT.
 * - accept: sin señales de manipulacion
 * - review: señales debiles, requiere revision humana
 * - reject: evidencia de manipulacion
 *
 * IMPORTANTE: el e-RUT no lleva firma criptografica, asi que 'accept' significa
 * "sin señales de manipulacion", NO "documento autentico". Solo el SII puede
 * confirmar autenticidad y su servicio esta protegido con captcha.
 */
export type ErutRecommendation = 'accept' | 'review' | 'reject';

export interface ErutFields {
  // Respaldados por el QR
  rut: string | null;
  razonSocial: string | null;
  direccion: string | null;
  serie: string | null;
  // NO respaldados por el QR: ninguna validacion local los cubre
  glosaActividad: string | null;
  fechaEmision: string | null;
  rutUsuario: string | null;
  usuario: string | null;
}

export interface ErutIntegrityCheck {
  id: string;
  description: string;
  passed: boolean;
  weight: number;
}

export interface ErutCrossCheck {
  allMatch: boolean;
  mismatchedFields: string[];
  unprotectedFields: string[];
}

export interface ErutAnalysisResponse {
  recommendation: ErutRecommendation;
  /** 0-100 ponderado. NO es probabilidad de autenticidad. */
  integrityScore: number;
  checks: ErutIntegrityCheck[];
  reasons: string[];
  notes: string[];
  fields: ErutFields;
  crossCheck: ErutCrossCheck;
  rutCheckDigitValid: boolean;
  /** SHA-256 del PDF, para la traza de auditoria */
  sha256: string;
  /** Siempre false: el e-RUT no tiene firma criptografica */
  verifiedWithSii: boolean;
}

/** Veredicto del análisis de liveness que devuelve el KYC backend. */
export interface AntiSpoofResult {
  /** 0-100. Es 100 - riskScore. Por debajo del mínimo el backend rechaza. */
  livenessScore: number;
  recommendation: 'accept' | 'review' | 'reject';
  rejectReason:
    | 'screen_recapture'
    | 'printed_photo'
    | 'multiple_faces'
    | 'no_face_detected'
    | 'low_liveness_score'
    | null;
  /** true si el backend bloqueó la selfie por este motivo. */
  blocked: boolean;
  riskScore: number;
  reasons: string[];
}

/**
 * Veredicto sobre la CALIDAD de la selfie: si esta foto daba para comparar.
 *
 * No decide nada — el backend nunca bloquea por esto. Sirve para que, cuando la
 * comparación no llega al umbral, se muestre el motivo real ("sacate los lentes")
 * en lugar de "tu rostro no coincide", que ante una selfie a contraluz o con
 * lentes de sol es falso y deja al usuario repitiendo el mismo error.
 *
 * El texto lo arma el KYC: es una sola fuente de verdad, igual que el
 * `userMessage` del liveness.
 */
export interface CalidadSelfie {
  suficiente: boolean;
  motivo:
    | 'sin_rostro'
    | 'lentes_de_sol'
    | 'rostro_tapado'
    | 'fuera_de_eje'
    | 'rostro_pequeno'
    | 'poca_nitidez'
    | 'poca_luz'
    | 'contraluz'
    | null;
  mensaje: string | null;
}

export interface FacialVerificationResponse {
  isMatch: boolean;
  similarity: number;
  threshold: number;
  sourceFaceDetected: boolean;
  targetFaceDetected: boolean;
  matches?: any[];
  /** Ver `CalidadSelfie`. Campo aditivo: puede no venir. */
  calidadSelfie?: CalidadSelfie;
  /**
   * Análisis de liveness de la selfie. Cuando el backend bloquea, este endpoint
   * responde 400 con `SELFIE_SPOOF_DETECTED` y no se llega a leer este campo; viene
   * poblado en las respuestas exitosas y en las que pasaron con observaciones.
   */
  antiSpoof?: AntiSpoofResult;
  metadata?: {
    processingTimeMs: number;
  };
}

@Injectable({
  providedIn: 'root',
})
export class KycApiService {
  private baseUrl = environment.apiKyc;

  constructor(
    private http: HttpClient,
    private preview: SignupPreviewService
  ) {}

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      skip: 'true', // Omitir interceptor de autenticación
    });
  }

  /**
   * Verifica si el servicio OTP WhatsApp está disponible
   */
  async checkOtpHealth(): Promise<{ status: string; service: string }> {
    const url = `${this.baseUrl}otp/health`;
    const headers = this.getHeaders();

    try {
      const response = await firstValueFrom(
        this.http.get<any>(url, { headers })
      );
      // El wrapper puede envolver la respuesta en .data
      return response.data || response;
    } catch (error: any) {
      throw {
        message: error?.error?.error?.message || error?.error?.message || 'Servicio de WhatsApp no disponible',
        statusCode: error?.status || 0,
      };
    }
  }

  /**
   * Envía OTP por WhatsApp
   * @param phoneNumber Número completo con código de país (ej: +56912345678)
   */
  async sendOtp(phoneNumber: string): Promise<SendOtpResponse> {
    const url = `${this.baseUrl}otp/send`;
    const body = { phoneNumber };
    const headers = this.getHeaders();

    try {
      const response = await firstValueFrom(
        this.http.post<KycApiWrapper<SendOtpResponse>>(url, body, { headers })
      );

      // Extraer data del wrapper
      if (response.data) {
        return response.data;
      }

      // Si no hay wrapper, devolver respuesta directa (fallback)
      return response as unknown as SendOtpResponse;
    } catch (error: any) {

      // Manejar respuesta envuelta en error
      if (error?.error?.data) {
        return { code: 'ERROR', ...error.error.data } as SendOtpResponse;
      }

      // Manejar error del wrapper
      if (error?.error?.error?.message) {
        return {
          success: false,
          code: 'ERROR',
          message: error.error.error.message,
        };
      }

      return {
        success: false,
        code: 'ERROR',
        message: error?.error?.message || 'Error al enviar el código. Intente nuevamente.',
      };
    }
  }

  /**
   * Verifica el código OTP
   * @param phoneNumber Número completo con código de país
   * @param code Código de 6 dígitos
   */
  async verifyOtp(phoneNumber: string, code: string): Promise<VerifyOtpResponse> {
    const url = `${this.baseUrl}otp/verify`;
    const body = { phoneNumber, code };
    const headers = this.getHeaders();

    try {
      const response = await firstValueFrom(
        this.http.post<KycApiWrapper<VerifyOtpResponse>>(url, body, { headers })
      );

      // Extraer data del wrapper
      if (response.data) {
        return response.data;
      }

      return response as unknown as VerifyOtpResponse;
    } catch (error: any) {

      // Manejar respuesta envuelta en error
      if (error?.error?.data) {
        return { code: 'ERROR', ...error.error.data } as VerifyOtpResponse;
      }

      if (error?.error?.error?.message) {
        return {
          success: false,
          code: 'ERROR',
          message: error.error.error.message,
        };
      }

      return {
        success: false,
        code: 'ERROR',
        message: error?.error?.message || 'Error al verificar el código. Intente nuevamente.',
      };
    }
  }

  /**
   * Reenvía OTP por WhatsApp
   * @param phoneNumber Número completo con código de país
   */
  async resendOtp(phoneNumber: string): Promise<SendOtpResponse> {
    const url = `${this.baseUrl}otp/resend`;
    const body = { phoneNumber };
    const headers = this.getHeaders();

    try {
      const response = await firstValueFrom(
        this.http.post<KycApiWrapper<SendOtpResponse>>(url, body, { headers })
      );

      // Extraer data del wrapper
      if (response.data) {
        return response.data;
      }

      return response as unknown as SendOtpResponse;
    } catch (error: any) {

      // Manejar respuesta envuelta en error
      if (error?.error?.data) {
        return { code: 'ERROR', ...error.error.data } as SendOtpResponse;
      }

      if (error?.error?.error?.message) {
        return {
          success: false,
          code: 'ERROR',
          message: error.error.error.message,
        };
      }

      return {
        success: false,
        code: 'ERROR',
        message: error?.error?.message || 'Error al reenviar el código. Intente nuevamente.',
      };
    }
  }

  /**
   * OCR de cédula chilena
   * @param file Archivo de imagen (PNG, JPG, PDF)
   * @param registro Datos del solicitante para persistir registro OCR
   */
  async ocrChile(file: File, registro: OcrRegistroData): Promise<OcrChileResponse> {
    // Preview del registro de empresa: no se manda el documento a ninguna parte.
    if (this.preview.activo) {
      return PREVIEW_OCR_CHILE;
    }

    const url = `${this.baseUrl}ocr-chile/cedula`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('cognitoId', registro.cognitoId);
    formData.append('tipo', registro.tipo);
    formData.append('pais', 'CHILE');
    if (registro.email) formData.append('email', registro.email);
    if (registro.whatsapp) formData.append('whatsapp', registro.whatsapp);
    if (registro.ip) formData.append('ip', registro.ip);

    const headers = new HttpHeaders({ skip: 'true' });

    try {
      const response = await firstValueFrom(
        this.http.post<KycApiWrapper<OcrChileResponse>>(url, formData, { headers })
      );

      // Extraer data del wrapper
      if (response.data) {
        return response.data;
      }

      return response as unknown as OcrChileResponse;
    } catch (error: any) {
      throw {
        message: error?.error?.error?.message || error?.error?.message || 'Error procesando documento chileno',
        code: error?.error?.error?.code || 'OCR_ERROR',
        statusCode: error?.status || 500,
      };
    }
  }

  /**
   * OCR de cédula venezolana
   * @param file Archivo de imagen (PNG, JPG, PDF)
   * @param registro Datos del solicitante para persistir registro OCR
   */
  async ocrVenezuela(file: File, registro: OcrRegistroData): Promise<OcrVenezuelaResponse> {
    const url = `${this.baseUrl}ocr-venezuela/cedula`;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('cognitoId', registro.cognitoId);
    formData.append('tipo', registro.tipo);
    formData.append('pais', 'VENEZUELA');
    if (registro.email) formData.append('email', registro.email);
    if (registro.whatsapp) formData.append('whatsapp', registro.whatsapp);
    if (registro.ip) formData.append('ip', registro.ip);

    const headers = new HttpHeaders({ skip: 'true' });

    try {
      const response = await firstValueFrom(
        this.http.post<KycApiWrapper<OcrVenezuelaResponse>>(url, formData, { headers })
      );

      // Extraer data del wrapper
      if (response.data) {
        return response.data;
      }

      return response as unknown as OcrVenezuelaResponse;
    } catch (error: any) {
      throw {
        message: error?.error?.error?.message || error?.error?.message || 'Error procesando documento venezolano',
        code: error?.error?.error?.code || 'OCR_ERROR',
        statusCode: error?.status || 500,
      };
    }
  }

  /**
   * Verificación facial (selfie vs documento)
   * @param selfie Archivo de imagen selfie
   * @param fotoDocumento Foto del documento en base64 (de OCR)
   * @param threshold Umbral de similitud (default 80)
   */
  async verifyFace(
    selfie: File,
    fotoDocumento: string,
    threshold: number = 80
  ): Promise<FacialVerificationResponse> {
    if (this.preview.activo) {
      return PREVIEW_FACIAL;
    }

    const url = `${this.baseUrl}facial-verification/verify`;
    const formData = new FormData();
    formData.append('selfie', selfie);
    formData.append('fotoDocumento', fotoDocumento);
    formData.append('threshold', threshold.toString());

    const headers = new HttpHeaders({ skip: 'true' });

    try {
      const response = await firstValueFrom(
        this.http.post<KycApiWrapper<FacialVerificationResponse>>(url, formData, { headers })
      );

      // Extraer data del wrapper
      if (response.data) {
        return response.data;
      }

      return response as unknown as FacialVerificationResponse;
    } catch (error: any) {
      throw {
        message: error?.error?.error?.message || error?.error?.message || 'Error en verificación facial',
        code: error?.error?.error?.code || 'FACE_VERIFICATION_ERROR',
        statusCode: error?.status || 500,
      };
    }
  }

  async checkSanctions(params: {
    fullName: string;
    firstName?: string;
    lastName?: string;
    nationality?: string;
    dateOfBirth?: string;
    threshold?: number;
  }): Promise<SanctionsCheckResult> {
    const url = `${this.baseUrl}sanctions/check`;
    const body: Record<string, any> = { fullName: params.fullName };
    if (params.firstName) body['firstName'] = params.firstName;
    if (params.lastName) body['lastName'] = params.lastName;
    if (params.nationality) body['nationality'] = params.nationality;
    if (params.dateOfBirth) body['dateOfBirth'] = params.dateOfBirth;
    if (params.threshold) body['threshold'] = params.threshold;
    const headers = this.getHeaders();

    try {
      const response = await firstValueFrom(
        this.http.post<KycApiWrapper<SanctionsCheckResult>>(url, body, { headers })
      );

      if (response.data) {
        return response.data;
      }

      return response as unknown as SanctionsCheckResult;
    } catch (error: any) {
      const statusCode = error?.status || 0;
      const message =
        error?.error?.error?.message ||
        error?.error?.message ||
        (statusCode === 0
          ? 'No se pudo conectar con el servicio de sanciones. Verifique su conexión.'
          : `Error consultando listas de sanciones (HTTP ${statusCode})`);
      throw {
        message,
        code: error?.error?.error?.code || 'SANCTIONS_CHECK_ERROR',
        statusCode,
      };
    }
  }

  /**
   * Analiza el PDF del e-RUT (Cedula RUT Electronica del SII).
   *
   * Extrae los campos de la capa de texto, decodifica el QR, los cruza y busca
   * señales de manipulacion. Solo acepta PDF.
   *
   * @param file PDF del e-RUT (maximo 10 MB)
   */
  async analyzeErut(file: File): Promise<ErutAnalysisResponse> {
    if (this.preview.activo) {
      return PREVIEW_ERUT;
    }

    const url = `${this.baseUrl}erut-chile/analyze`;
    const formData = new FormData();
    formData.append('file', file);

    const headers = new HttpHeaders({ skip: 'true' });

    try {
      const response = await firstValueFrom(
        this.http.post<KycApiWrapper<ErutAnalysisResponse>>(url, formData, { headers })
      );

      if (response.data) {
        return response.data;
      }

      return response as unknown as ErutAnalysisResponse;
    } catch (error: any) {
      throw {
        message:
          error?.error?.error?.message ||
          error?.error?.message ||
          'Error analizando el e-RUT. Intente nuevamente.',
        code: error?.error?.error?.code || 'ERUT_ANALYSIS_ERROR',
        statusCode: error?.status || 500,
      };
    }
  }

  /**
   * @param duranteRegistro Saca la llamada del manejo global de sesion:
   *   `skip` → ApiRestInterceptor no adjunta el token de Cognito.
   *   `X-Skip-Error-Interceptor` → ErrorInterceptor no trata un 401 como
   *   "Sesion expirada", que cerraria la sesion y expulsaria al usuario al login
   *   a mitad del alta. El componente maneja el fallo por su cuenta: si la
   *   consulta PEP falla, queda la declaracion manual.
   */
  async checkPepChile(
    params: PepChileCheckRequest,
    duranteRegistro = false
  ): Promise<PepChileCheckResult> {
    if (this.preview.activo) {
      return PREVIEW_PEP_CHILE;
    }

    const url = `${this.baseUrl}pep-chile/check`;
    const options = duranteRegistro
      ? {
          headers: new HttpHeaders({
            skip: 'true',
            'X-Skip-Error-Interceptor': 'true',
          }),
        }
      : {};

    try {
      const response = await firstValueFrom(
        this.http.post<KycApiWrapper<PepChileCheckResult>>(url, params, options)
      );

      if (response.data) {
        return response.data;
      }

      return response as unknown as PepChileCheckResult;
    } catch (error: any) {
      const statusCode = error?.status || 0;
      const message =
        error?.error?.error?.message ||
        error?.error?.message ||
        (statusCode === 0
          ? 'No se pudo conectar con el servicio PEP Chile. Verifique su conexión.'
          : `Error consultando PEP Chile (HTTP ${statusCode})`);
      throw {
        message,
        code: error?.error?.error?.code || 'PEP_CHILE_CHECK_ERROR',
        statusCode,
      };
    }
  }
}
